import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle2,
  Lock,
  Mail,
  ArrowLeft,
  Sparkles,
  Eye,
  EyeOff,
} from "lucide-react";
import { sendOtp, verifyOtp, resetPassword } from "../../services/api";

export default function ForgotPassword() {
  const [error, setError] = useState("");
  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState(1);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  // Timer effect
  useEffect(() => {
    let interval;
    if (timer > 0 && step === 2) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, step]);

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 5) {
      const nextInput = document.querySelector(
        `input[name="otp-${index + 1}"]`
      );
      if (nextInput) nextInput.focus();
    }
  };
  const handleOtpKeyDown = (e, index) => {
  if (e.key === "Backspace" && otp[index] === "" && index > 0) {
    const prevInput = document.querySelector(
      `input[name="otp-${index - 1}"]`
    );
    if (prevInput) prevInput.focus();
  }
};


  const handleSendOtp = async () => {
    if (!emailOrPhone.trim()) {
      setError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailOrPhone)) {
      setError("Please enter a valid email");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await sendOtp({
        email: emailOrPhone.toLowerCase().trim(),
      });

      console.log("Send OTP Response:", response);

      toast.success("OTP sent to your Email!");
      setStep(2);
      setTimer(30);
      setCanResend(false);
    } catch (error) {
      console.error("Send OTP error:", error);
      console.error("Error response:", error.response);

      let errorMessage = "Failed to send OTP. Please try again.";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.msg ||
          errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      toast.error("Please enter complete OTP");
      return;
    }

    setIsLoading(true);

    try {
      const response = await verifyOtp({
        email: emailOrPhone.toLowerCase().trim(),
        otp: otpString,
      });

      console.log("Verify OTP Response:", response);

      if (response.data?.token || response.data?.verificationToken) {
        setVerificationToken(
          response.data.token || response.data.verificationToken
        );
      }

      toast.success("OTP Verified!");
      setStep(3);
      setTimer(0);
    } catch (error) {
      console.error("Verify OTP error:", error);
      console.error("Error response:", error.response);

      let errorMessage = "Invalid OTP. Please try again.";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.msg ||
          errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;

    setIsLoading(true);

    try {
      await sendOtp({ email: emailOrPhone.toLowerCase().trim() });
      toast.success("OTP resent successfully!");
      setOtp(["", "", "", "", "", ""]);
      setTimer(15);
      setCanResend(false);
    } catch (error) {
      console.error("Resend OTP error:", error);
      toast.error("Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setPasswordError("");

    if (!newPass || !confirmPass) {
      setPasswordError("Please fill in both fields");
      return;
    }
    const strongPasswordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{6,}$/;

if (!strongPasswordRegex.test(newPass)) {
  setPasswordError(
    "Password must be at least 6 characters and include uppercase, lowercase, number & special character"
  );
  return;
}

    if (newPass !== confirmPass) {
      setPasswordError("Passwords do not match");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        email: emailOrPhone.toLowerCase().trim(),
        newPassword: newPass,
        otp: otp.join(""),
      };

      if (verificationToken) {
        payload.token = verificationToken;
      }

      console.log("Reset Password Payload:", payload);

      const response = await resetPassword(payload);

      console.log("Reset Password Response:", response);

      toast.success("Password reset successful!");

      setTimeout(() => {
        navigate("/login", {
          state: {
            message:
              "Password reset successful! Please login with your new password.",
            email: emailOrPhone,
          },
        });
      }, 1500);
    } catch (error) {
      console.error("Reset password error:", error);
      console.error("Error response:", error.response);

      let errorMessage = "Failed to reset password. Please try again.";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.msg ||
          errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setPasswordError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen flex bg-[#FFF9F0] overflow-hidden scale-75 origin-top pt-8 mt-16">
      {/* LEFT SIDE UI */}
      <div className="hidden lg:flex w-1/2 min-h-screen bg-gradient-to-br from-orange-600 via-red-600 to-orange-500 relative flex-col justify-center items-center text-center px-10 overflow-hidden">
        <div className="relative z-10 max-w-lg mx-auto flex flex-col items-center text-white px-4">
          <div className="w-24 h-24 bg-white/20 rounded-full flex justify-center items-center backdrop-blur mb-6">
            <Lock className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl xl:text-4xl font-serif font-bold mb-4">
            Secure Account Recovery
          </h1>
          <p className="text-white/90 mb-8 text-base xl:text-lg">
            Reset your password safely and continue connecting with verified
            Purohits.
          </p>

          <div className="space-y-4 text-left">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6" />
              <p>Encrypted verification process</p>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6" />
              <p>Instant OTP delivery</p>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6" />
              <p>24/7 account security</p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex-1 min-h-screen flex justify-center items-center p-4 lg:p-10 overflow-y-auto">
        <div className="scale-105 origin-top w-full max-w-md mx-auto">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-gray-600 hover:text-orange-600 mb-4 font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Sign in
          </button>

          {/* STEPS UI */}
          <div className="flex justify-center mb-2">
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((num) => (
                <div key={num} className="flex items-center">
                  <div
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${
                      num === step
                        ? "bg-orange-500 text-white shadow-lg scale-110"
                        : num < step
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {num === step ? <Sparkles className="w-5 h-5" /> : num}
                  </div>

                  {num !== 3 && (
                    <div
                      className={`h-1 w-10 mx-2 rounded-full ${
                        num < step ? "bg-green-500" : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <p className="text-center text-gray-600 mb-6 text-sm sm:text-base">
            Step {step} of 3
          </p>

          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 w-full">
            {/* STEP 1 – ENTER EMAIL */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-serif font-bold">
                  Reset Your Password
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Enter your registered Email.
                </p>

                <label className="text-sm font-semibold">Email</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-orange-500">
                    <Mail />
                  </span>
                  <input
                    type="email"
                    value={emailOrPhone}
                    onChange={(e) => {
                      setEmailOrPhone(e.target.value);
                      setError("");
                    }}
                    placeholder="your.email@example.com"
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl outline-none ${
                      error
                        ? "border-red-500"
                        : "border-gray-300 focus:border-orange-500"
                    }`}
                    disabled={isLoading}
                  />
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}

                <button
                  onClick={handleSendOtp}
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Sending..." : "Send Verification Code"}
                </button>

                <p className="text-center text-sm text-gray-600">
                  Remember your password?{" "}
                  <button
                    onClick={() => navigate("/login")}
                    className="text-orange-600 font-medium hover:underline"
                  >
                    Sign in here
                  </button>
                </p>
              </div>
            )}

            {/* STEP 2 – OTP */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-serif font-bold">
                  Verify Your Identity
                </h2>

                <p className="text-gray-600 text-sm sm:text-base">
                  We've sent a 6-digit code to{" "}
                  <span className="font-semibold">{emailOrPhone}</span>
                </p>

                <div className="flex gap-2 sm:gap-3 justify-center">
                  {otp.map((val, i) => (
                    <input
                      key={i}
                      name={`otp-${i}`}
                      maxLength="1"
                      value={val}
                      onChange={(e) => handleOtpChange(e.target.value, i)}
                      onKeyDown={(e) => handleOtpKeyDown(e, i)}
                      className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg font-semibold border-2 border-gray-300 rounded-xl focus:border-orange-500 outline-none"
                      disabled={isLoading}
                    />
                  ))}
                </div>

                <p className="text-center text-sm text-gray-600">
                  {canResend ? (
                    <button
                      className="text-orange-600 font-medium hover:underline disabled:opacity-50"
                      onClick={handleResendOtp}
                      disabled={isLoading}
                    >
                      {isLoading ? "Sending..." : "Resend Code"}
                    </button>
                  ) : (
                    <span>
                      Not received your code?{" "}
                      <span className="text-orange-600 font-semibold">
                        00:{timer}
                      </span>
                    </span>
                  )}
                </p>

                <button
                  disabled={otp.join("").length !== 6 || isLoading}
                  onClick={handleVerifyOtp}
                  className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Verifying..." : "Verify & Continue"}
                </button>
              </div>
            )}

            {/* STEP 3 – CREATE NEW PASSWORD */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-2xl sm:text-3xl font-serif font-bold">
                  Create New Password
                </h2>

                <p className="text-gray-600 text-sm sm:text-base">
                  Choose a strong password for your account.
                </p>

                {/* NEW PASSWORD */}
                <div>
                  <label className="text-sm font-semibold">New Password</label>
                  <div className="relative">
                    <input
                      type={showNew ? "text" : "password"}
                      value={newPass}
                      onChange={(e) => {
                        setNewPass(e.target.value);
                        setPasswordError("");
                      }}
                      placeholder="Enter new password (min. 6 characters)"
                      className="w-full pl-4 pr-12 py-3 border-2 border-gray-300 rounded-xl focus:border-orange-500 outline-none"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showNew ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                </div>

                {/* CONFIRM PASSWORD */}
                <div>
                  <label className="text-sm font-semibold">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? "text" : "password"}
                      value={confirmPass}
                      onChange={(e) => {
                        setConfirmPass(e.target.value);
                        setPasswordError("");
                      }}
                      placeholder="Confirm your password"
                      className="w-full pl-4 pr-12 py-3 border-2 border-gray-300 rounded-xl focus:border-orange-500 outline-none"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showConfirm ? <EyeOff /> : <Eye />}
                    </button>
                  </div>

                  <div className="min-h-[20px] mt-1">
                    {passwordError && (
                      <p className="text-red-500 text-sm">{passwordError}</p>
                    )}
                  </div>
                </div>

                {/* SUBMIT BUTTON */}
                <button
                  onClick={handleResetPassword}
                  disabled={isLoading}
                  className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              Need help recovering your account?
            </p>
            <a
              href="mailto:support@purohitapp.com"
              className="text-orange-600 flex items-center justify-center gap-2 mt-1 hover:underline"
            >
              <Mail className="w-4 h-4" /> Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
